import { io, Socket } from 'socket.io-client';
import { logger } from '@/lib/monitoring/logger';
import { SOCKET_EVENTS } from '@/lib/constants/socket';
import { toast } from 'react-hot-toast';
import type {
  AuctionRoom,
  SocketResponse,
  Player,
  Team,
} from '@/lib/types/auction';
import { WS_BASE_URL } from '@/services/api/endpoints';
import { analytics } from '@/lib/monitoring/analytics';

export type EventMap = {
  [SOCKET_EVENTS.AUCTION_STATUS]: AuctionRoom;
  [SOCKET_EVENTS.BID_PLACED]: AuctionRoom;
  [SOCKET_EVENTS.PLAYER_SOLD]: {
    player: Player;
    team: Team;
    amount: number;
    timestamp: string;
  };
  [SOCKET_EVENTS.PLAYER_UNSOLD]: {
    player: Player;
    status: string;
    timestamp: string;
  };
  [SOCKET_EVENTS.POOL_COMPLETED]: {
    poolId: string;
    poolName: string;
  };
  [SOCKET_EVENTS.NO_PLAYERS_LEFT]: {
    tournamentId: string;
  };
  [SOCKET_EVENTS.ERROR]: { message: string };
  [SOCKET_EVENTS.CONNECT]: void;
  [SOCKET_EVENTS.DISCONNECT]: void;
};

type EventCallback<T> = (data: SocketResponse<T>) => void;
type EventHandler<T> = (data: SocketResponse<T>) => void;

class SocketService {
  private socket: Socket | null = null;
  private handlerMap: Map<EventCallback<unknown>, EventHandler<unknown>> =
    new Map();
  private currentTournamentId: string | null = null;
  private isConnecting: boolean = false;
  private currentRoom: AuctionRoom | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private keepAliveInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;

  private getReconnectDelay(): number {
    return Math.min(1000 * Math.pow(2, this.reconnectAttempts), 60000);
  }

  async connect(token: string | null, tournamentId: string): Promise<void> {
    if (!tournamentId) {
      toast.error('Tournament ID is required');
      return;
    }

    if (this.socket?.connected && this.currentTournamentId === tournamentId) {
      logger.info('Already connected to tournament', { tournamentId });
      return;
    }

    if (this.isConnecting) {
      logger.info('Connection already in progress, waiting...');
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.isConnecting = true;

    try {
      if (this.socket) {
        logger.info('Cleaning up existing socket');
        this.cleanupInternal();
      }

      logger.info('Connecting to WebSocket server:', {
        wsUrl: WS_BASE_URL,
        tournamentId,
        attempt: this.reconnectAttempts + 1,
      });

      return new Promise((resolve, reject) => {
        this.socket = io(WS_BASE_URL, {
          auth: { token },
          query: { tournamentId },
          transports: ['websocket'],
          reconnection: false,
          timeout: 5000,
          withCredentials: true,
          forceNew: true,
          autoConnect: true,
          path: '/socket.io',
        });

        const connectionTimeout = setTimeout(() => {
          if (!this.socket?.connected) {
            this.isConnecting = false;
            this.handleReconnect(token, tournamentId);
            reject(new Error('Connection timeout'));
          }
        }, 5000);

        this.socket.once('connect', () => {
          clearTimeout(connectionTimeout);
          this.currentTournamentId = tournamentId;
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.setupListeners();
          this.setupKeepAlive();
          resolve();
        });

        this.socket.once('connect_error', (error) => {
          clearTimeout(connectionTimeout);
          this.isConnecting = false;
          this.handleReconnect(token, tournamentId);

          if (
            error.message.includes('unauthorized') ||
            error.message.includes('unauthenticated')
          ) {
            reject(error);
          } else {
            toast.error('Failed to connect: ' + error.message);
            reject(error);
          }
        });
      });
    } catch (error) {
      this.isConnecting = false;
      if (
        error instanceof Error &&
        (error.message.includes('unauthorized') ||
          error.message.includes('unauthenticated'))
      ) {
        throw error;
      }
      this.handleReconnect(token, tournamentId);
      throw error;
    }
  }

  private handleReconnect(token: string | null, tournamentId: string) {
    this.reconnectAttempts++;

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      toast.error('Maximum reconnection attempts reached');
      return;
    }

    const delay = this.getReconnectDelay();
    logger.info('Scheduling reconnect', {
      attempt: this.reconnectAttempts,
      delay,
      maxAttempts: this.maxReconnectAttempts,
    });

    this.reconnectTimeout = setTimeout(() => {
      this.connect(token, tournamentId);
    }, delay);
  }

  private cleanupInternal() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval as NodeJS.Timeout);
      this.keepAliveInterval = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.close();
      this.socket = null;
    }

    this.handlerMap.clear();
    this.currentRoom = null;
    this.isConnecting = false;
  }

  private setupKeepAlive() {
    if (!this.socket) return;

    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval as NodeJS.Timeout);
    }

    this.keepAliveInterval = setInterval(() => {
      if (this.socket?.connected) {
        this.socket.emit('ping');
        logger.debug('Ping sent to keep connection alive');
      }
    }, 25000);

    this.socket.on('disconnect', () => {
      if (this.keepAliveInterval) {
        clearInterval(this.keepAliveInterval as NodeJS.Timeout);
        this.keepAliveInterval = null;
      }
    });
  }

  public cleanup() {
    logger.info('Cleaning up socket connection');
    this.cleanupInternal();
  }

  private setupListeners() {
    if (!this.socket) return;

    this.socket.on(SOCKET_EVENTS.CONNECT, () => {
      logger.info('Socket connected successfully');
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      if (this.currentTournamentId) {
        this.joinAuction(this.currentTournamentId);
      }
    });

    this.socket.on(SOCKET_EVENTS.DISCONNECT, (reason) => {
      logger.warn('Socket disconnected:', { reason });
      this.isConnecting = false;
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        toast.error('Maximum reconnection attempts reached');
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      toast.error('Connection error: ' + error.message);
      this.isConnecting = false;
      this.reconnectAttempts++;
    });

    this.socket.on(SOCKET_EVENTS.AUCTION_STATUS, (data: AuctionRoom) => {
      logger.info('Received auction status', { status: data.status });
      this.currentRoom = data;
      analytics.trackAuctionEvent('status_change', {
        status: data.status,
        tournamentId: data.tournament?.id,
        currentPlayerId: data.currentPlayer?.id,
      });
    });

    this.socket.on(SOCKET_EVENTS.BID_PLACED, (data: AuctionRoom) => {
      analytics.trackBidEvent({
        tournamentId: data.tournament?.id,
        playerId: data.currentPlayer?.id,
        currentBid: data.currentBid,
        bidder: data.currentBid?.team?.name,
      });
    });

    this.socket.on(
      SOCKET_EVENTS.PLAYER_SOLD,
      (data: { player: Player; team: Team; amount: number }) => {
        analytics.trackAuctionEvent('player_sold', {
          playerId: data.player?.id,
          playerName: data.player?.user?.name,
          teamId: data.team?.id,
          teamName: data.team?.name,
          amount: data.amount,
        });
      }
    );

    this.socket.on(
      SOCKET_EVENTS.PLAYER_UNSOLD,
      (data: { player: Player; status: string }) => {
        logger.info('Player unsold', {
          player: data.player,
          status: data.status,
        });
      }
    );

    this.socket.on(
      SOCKET_EVENTS.POOL_COMPLETED,
      (data: { poolId: string; poolName: string }) => {
        logger.info('Pool completed', {
          poolId: data.poolId,
          poolName: data.poolName,
        });
      }
    );

    this.socket.on(
      SOCKET_EVENTS.NO_PLAYERS_LEFT,
      (data: { tournamentId: string }) => {
        logger.info('No players left', { tournamentId: data.tournamentId });
      }
    );

    this.socket.on(SOCKET_EVENTS.ERROR, (error: unknown) => {
      let message = 'Socket error occurred';
      if (error && typeof error === 'object') {
        const err = (error as { error?: { errorMessage?: string } }).error;
        if (
          err &&
          typeof err === 'object' &&
          'errorMessage' in err &&
          err.errorMessage
        ) {
          message = String(err.errorMessage);
        }
      }
      if (message !== 'Socket error occurred') {
        toast.error(message);
      }
      return false;
    });

    this.socket.onAny((event, ...args) => {
      logger.debug('Socket event received', { event, args });
    });
  }

  getSocketInstance(): Socket | null {
    return this.socket;
  }

  isConnectedToAuction(tournamentId: string): boolean {
    return (
      this.socket?.connected === true &&
      this.currentTournamentId === tournamentId &&
      !this.isConnecting
    );
  }

  getCurrentRoom(): AuctionRoom | null {
    return this.currentRoom;
  }

  joinAuction(tournamentId: string) {
    if (!this.socket?.connected) {
      logger.warn('Cannot join auction: socket not connected');
      return;
    }

    this.currentTournamentId = tournamentId;
    logger.info('Joining auction room', { tournamentId });
    this.socket.emit(SOCKET_EVENTS.JOIN_AUCTION, { tournamentId });
  }

  leaveAuction(tournamentId: string) {
    if (!this.socket?.connected) return;
    logger.info('Leaving auction room', { tournamentId });
    this.socket.emit(SOCKET_EVENTS.LEAVE_AUCTION, { tournamentId });
  }

  on<K extends keyof EventMap>(event: K, callback: EventCallback<EventMap[K]>) {
    if (!this.socket) return;

    const handler: EventHandler<EventMap[K]> = (
      data: SocketResponse<EventMap[K]>
    ) => {
      callback(data);
    };

    this.handlerMap.set(
      callback as EventCallback<unknown>,
      handler as EventHandler<unknown>
    );
    this.socket.on(event as string, handler);
  }

  off<K extends keyof EventMap>(
    event: K,
    callback: EventCallback<EventMap[K]>
  ) {
    if (!this.socket) return;

    const handler = this.handlerMap.get(callback as EventCallback<unknown>);
    if (handler) {
      this.socket.off(event as string, handler);
      this.handlerMap.delete(callback as EventCallback<unknown>);
    }
  }

  optOut(data: { teamId: string; playerId: string; tournamentId: string }) {
    if (!this.socket?.connected) {
      toast.error('Cannot opt out: socket not connected');
      return;
    }
    logger.info('Team opting out', {
      teamId: data.teamId,
      playerId: data.playerId,
    });
    this.socket.emit(SOCKET_EVENTS.TEAM_OPTED_OUT, data);
  }

  startAuction(data: {
    poolId: string;
    tournamentId: string;
    timeout: number;
    availablePlayerIds: string[];
  }) {
    if (!this.socket?.connected) {
      toast.error('Cannot start auction: socket not connected');
      return;
    }
    logger.info('Starting pool auction', data);
    this.socket.emit(SOCKET_EVENTS.START_AUCTION, data);
  }
}

export const socketService = new SocketService();
